import {
	MAX_ASSETS,
	templateAssetRange,
	type SponsorOptions,
	type TakeoverTemplateKey,
} from "sponsor/sponsorBooking";
import {
	sponsorOptionsForWire,
	validateSponsorOptions,
	type SponsorOptionErrors,
} from "sponsor/sponsorOptions";
import {ImageCompressionError} from "core/images/imageCompression";
import {
	classifyRenderedFailure,
	RenderedFailureKey,
	type SafeFailureDiagnostic,
} from "utils/renderedFailure";
import type {AccountMessages} from "../../i18n/en/account";

/**
 * Pure page-theme validation (api#511, epic #26 "logic outside React"): the preconditions the theme
 * editor checks before it spends a round-trip.
 *
 * Same contract as its sibling `brandValidation.ts` — these rules MIRROR the server's, they do not
 * replace it. The api re-validates every write and sniffs each image's magic bytes (a `File.type` is
 * only a guess the browser made from a filename), so it remains the one thing that can reject a payload
 * for real. What the client buys is an instant, translated message instead of a 400.
 *
 * Every verdict carries an error KEY, never translated copy: the catalog lookup
 * (`m.account.theme.errors[key]`) stays in the React layer.
 */

/**
 * The image types the api will store. SVG is deliberately absent and must stay absent — it is scriptable
 * XML served back from our own origin.
 */
export const ACCEPTED_THEME_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;

/** The `accept` attribute for the file picker, derived so the two can never disagree. */
export const THEME_IMAGE_ACCEPT_ATTRIBUTE = ACCEPTED_THEME_IMAGE_TYPES.join(",");

/**
 * The largest set any template animates. Shared with the sponsor booking cap rather than restated: both
 * feed the same three effects, so a second number here could only ever be the wrong one.
 */
export const MAX_THEME_IMAGES = MAX_ASSETS;

/**
 * A key into the `account.theme.errors` catalog namespace. Derived from the English catalog so renaming
 * or dropping a message is a compile error here rather than a runtime `undefined`.
 */
export type ThemeErrorKey = keyof AccountMessages["account"]["theme"]["errors"];

/** Locale-independent selector retained by a multi-image upload report. */
export type ThemeUploadFailureMessage =
	| {catalog: "theme"; messageKey: ThemeErrorKey}
	| {catalog: "common"; messageKey: RenderedFailureKey};

export interface ClassifiedThemeUploadFailure {
	message: ThemeUploadFailureMessage;
	/** Null for expected local validation failures; otherwise the bounded diagnostic worth reporting. */
	diagnostic: SafeFailureDiagnostic | null;
}

/**
 * The image fields the rules read. Structural, so tests need no real `File`.
 *
 * Size is deliberately absent: since ui#601 an oversized backdrop is COMPRESSED rather than refused, so
 * no byte rule remains for a sender to fail. `BACKDROP_IMAGE_TARGET` carries the api's ceiling now.
 */
export type ThemeImageDraft = {
	type: string;
};

export type ThemeVerdict =
	| {valid: true}
	| {valid: false; error: ThemeErrorKey};

/**
 * Whether one picked image is a TYPE the library stores.
 *
 * The library cap is no longer checked here (ui#664). A pick is now a batch, and how many of it fit is
 * decided once for the whole pick by `admitPickedFiles` — the leading images are admitted and the rest
 * reported as refused, which a per-image count check could only turn back into "all or nothing". What is
 * left is genuinely per image, and stays per image.
 *
 * Size is absent for the older reason: since ui#601 an oversized backdrop is COMPRESSED rather than
 * refused, so no byte rule remains for a sender to fail.
 */
export function validateThemeImage(image: ThemeImageDraft): ThemeVerdict {
	if (!ACCEPTED_THEME_IMAGE_TYPES.includes(image.type as (typeof ACCEPTED_THEME_IMAGE_TYPES)[number])) {
		return {valid: false, error: "imageUnsupported"};
	}
	return {valid: true};
}

/**
 * One picked image the library refused, carrying the key that says why.
 *
 * An ERROR rather than a returned verdict because a multi-file pick runs each image through
 * `processPickedBatch`, which records a per-file outcome by catching what its step throws. A refusal and
 * a failed upload therefore have to arrive the same way, or the type check would need a second path back
 * out of the batch that reported nothing.
 */
export class ThemeImageRejected extends Error {
	constructor(readonly key: ThemeErrorKey) {
		super(key);
		this.name = "ThemeImageRejected";
	}
}

/** Maps every per-image failure to catalog state without retaining decoder, API, or provider prose. */
export function classifyThemeUploadFailure(error: unknown): ClassifiedThemeUploadFailure {
	if (error instanceof ThemeImageRejected) {
		return {message: {catalog: "theme", messageKey: error.key}, diagnostic: null};
	}
	if (error instanceof ImageCompressionError) {
		return {message: {catalog: "theme", messageKey: "imageUnprocessable"}, diagnostic: null};
	}
	const failure = classifyRenderedFailure(error, RenderedFailureKey.SaveFailed);
	return {
		message: {catalog: "common", messageKey: failure.messageKey},
		diagnostic: failure.diagnostic,
	};
}

/**
 * The images a treatment actually animates, out of everything the sender has chosen: as many as it can
 * take, from the front (ui#583).
 *
 * The sender's images are a WORKING SET, not a per-treatment list. That is already how the picker
 * behaves — `previewBackdropArt` has always clamped the preview to the template's maximum, so a sender
 * with three images who selects Hero is shown a working hero of their first one. Publishing used to
 * refuse that exact draft as "too many images", which left the Publish control greyed out with a live
 * preview beside it promising the opposite.
 *
 * The two agree on every draft that can be published, which is the guarantee that matters, but they are
 * NOT the same function and this one is not a rewrite of the other. `previewBackdropArt` also pads UPWARD
 * — under its minimum it repeats what it has, so one image previews as a three-plane cascade — and a
 * publish must never do that: the api dedupes `assetIds` through a LinkedHashSet, so a repeated id would
 * arrive as one image and fail the arity it was padded to satisfy. Below the minimum the draft is simply
 * not publishable ({@link isThemePublishable}), so the padded preview is never a promise about a write.
 * Closing the gap the other way — a preview that stops padding — is a separate question about what a
 * half-filled treatment should look like.
 *
 * Generic in the element type so the hook can slice assets and a test can slice plain strings; nothing
 * here reads a field.
 */
export function themeImagesPublished<T>(template: TakeoverTemplateKey, chosen: readonly T[]): T[] {
	return chosen.slice(0, templateAssetRange(template).max);
}

/**
 * Validates the draft as a whole: each treatment needs a minimum number of images to BE itself — a deck
 * of one image has nothing to cycle to. The arity is the template's own, from `sponsor/sponsorBooking.ts`,
 * so the picker's hint and this check read one table.
 *
 * There is deliberately NO upper check here any more (ui#583). The maximum is not a thing a sender can be
 * wrong about: it is satisfied by construction, because {@link themeImagesPublished} is what the write
 * sends. The cap that IS a sender's business — {@link MAX_THEME_IMAGES}, how many they may keep at all —
 * is enforced where they can act on it, at the moment they pick: `admitPickedFiles` admits only what the
 * library still holds and reports the rest (ui#664). The api enforces it again on every upload.
 */
export function validateThemeDraft(template: TakeoverTemplateKey, imageCount: number): ThemeVerdict {
	if (imageCount < templateAssetRange(template).min) {
		return {valid: false, error: "tooFewImages"};
	}
	return {valid: true};
}

/**
 * The wallpaper settings a sender may save, judged by the SHARED table (kitecorp/transferify-ui#1869).
 *
 * A thin delegation on purpose, and the delegation is the point rather than an economy. The api decides
 * this in one `BackdropOptionsValidator` for both features it serves — a bought takeover and a sender's
 * own page theme — so a second rule set on this side would be a second opinion that eventually
 * disagrees with the controls it is judging: `sponsor/options/SponsorOptionsPanel.tsx` builds those
 * controls from `TEMPLATE_OPTIONS`, and this asks the very function that reads the same table.
 *
 * Returns the sponsor catalogue's KEYS, not this catalogue's, because the refusals are the same
 * refusals; the page theme translates them through its own `optionErrors` copy so a sender never reads
 * an advertiser's wording.
 */
export function validateThemeOptions(
	template: TakeoverTemplateKey, options: SponsorOptions,
): SponsorOptionErrors {
	return validateSponsorOptions(template, options);
}

/**
 * The settings a publish actually SENDS: normalised, with every key the treatment does not take dropped,
 * and `null` for a backdrop — which is what the api stores for one.
 *
 * Dropping rather than refusing, exactly as the booking form does. A sender who lays out a wipe and then
 * moves the picker back to a carousel has not made a mistake to be told about: the settings are simply
 * not sent, and are still in the draft if they move back.
 */
export function themeOptionsForWire(
	template: TakeoverTemplateKey, options: SponsorOptions,
): SponsorOptions | null {
	return sponsorOptionsForWire(template, options);
}

/**
 * Whether the draft can be published as it stands: enough images for the treatment, and no setting the
 * api would refuse.
 *
 * The settings are judged as they will be SENT rather than as they are held, which is why a backdrop
 * draft still carrying a wallpaper's leftovers is publishable — {@link themeOptionsForWire} drops them,
 * so there is nothing for the api to refuse and nothing on screen for the sender to correct.
 */
export function isThemePublishable(
	template: TakeoverTemplateKey, imageCount: number, options: SponsorOptions,
): boolean {
	return validateThemeDraft(template, imageCount).valid
		&& Object.keys(themeSettingErrors(template, options)).length === 0;
}

/**
 * The refusals a publish would actually earn: the settings judged AS THEY WILL BE SENT.
 *
 * Named once because two callers need the same answer — {@link isThemePublishable} to decide whether
 * Publish is offered, and the editor to say which control is wrong — and a second spelling of "wire it,
 * then judge it" is how the button and the message under the control come to disagree.
 */
export function themeSettingErrors(
	template: TakeoverTemplateKey, options: SponsorOptions,
): SponsorOptionErrors {
	return validateThemeOptions(template, themeOptionsForWire(template, options) ?? {});
}
